import { useEffect, useRef, type FocusEvent, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

type MultiSelectDropdownProps<TSelected, TOption> = {
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  placeholder: string;
  selected: TSelected[];
  options: TOption[];
  selectedKey: (item: TSelected) => string | number;
  optionKey: (item: TOption) => string | number;
  optionLabel: (item: TOption) => string;
  onOptionToggle: (item: TOption) => void;
  renderSelected: (item: TSelected, index: number) => ReactNode;
};

/**
 * Controlled multi-select shell used by job creation fields. The parent owns
 * which picker is open, while this component owns consistent dismiss/focus
 * behavior. Toggling an option intentionally never dismisses the menu.
 */
export function MultiSelectDropdown<TSelected, TOption>({
  id,
  open,
  onOpenChange,
  disabled = false,
  placeholder,
  selected,
  options,
  selectedKey,
  optionKey,
  optionLabel,
  onOptionToggle,
  renderSelected,
}: MultiSelectDropdownProps<TSelected, TOption>) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener("pointerdown", dismissOutside, true);
    return () => document.removeEventListener("pointerdown", dismissOutside, true);
  }, [open, onOpenChange]);

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) onOpenChange(false);
  }

  return (
    <div ref={rootRef} className="relative" onBlurCapture={handleBlur} data-testid={`${id}-root`}>
      <div className="min-h-10 space-y-1 rounded-md border bg-background p-1">
        {selected.map(renderSelected)}
        <button
          type="button"
          disabled={disabled}
          aria-label={placeholder}
          aria-expanded={open}
          aria-controls={`${id}-options`}
          onClick={() => onOpenChange(!open)}
          className="flex h-8 w-full items-center justify-between rounded px-2 text-left text-sm text-muted-foreground hover:bg-accent"
        >
          <span>{placeholder}</span>
          {open ? (
            <ChevronUp aria-hidden="true" data-testid={`${id}-chevron-up`} className="size-4 shrink-0" />
          ) : (
            <ChevronDown aria-hidden="true" data-testid={`${id}-chevron-down`} className="size-4 shrink-0" />
          )}
        </button>
      </div>
      {open && (
        <div
          id={`${id}-options`}
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-card p-1 shadow-lg"
        >
          {options.map((option) => {
            const key = optionKey(option);
            const checked = selected.some((item) => selectedKey(item) === key);
            return (
              <div
                key={key}
                role="checkbox"
                aria-checked={checked}
                tabIndex={disabled ? -1 : 0}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm hover:bg-accent"
                onClick={() => {
                  if (!disabled) onOptionToggle(option);
                }}
                onKeyDown={(event) => {
                  if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
                  event.preventDefault();
                  onOptionToggle(option);
                }}
              >
                <input
                  type="checkbox"
                  aria-hidden="true"
                  tabIndex={-1}
                  data-testid={`${id}-option-${key}-checkbox`}
                  checked={checked}
                  onChange={() => onOptionToggle(option)}
                  onClick={(event) => event.stopPropagation()}
                  disabled={disabled}
                />
                <span className="min-w-0 flex-1 truncate">{optionLabel(option)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
